import React, { useEffect, useState } from "react";
import { CheckCircle, X } from "lucide-react";

interface SuccessMessageProps {
  message: string | null;
  onClose?: () => void;
  duration?: number;
  variant?: 'default' | 'blue' | 'yellow';
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({
  message,
  onClose,
  duration = 5000,
  variant,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  // Keep the last non-null message so the slide-out animation still shows
  // text instead of flashing an empty shell while message has gone to null.
  const [lastMessage, setLastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (message) {
      setLastMessage(message);
      setShouldRender(true);
      const showTimer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(showTimer);
    }
    // Message cleared externally — slide out then unmount.
    setIsVisible(false);
    const hideTimer = setTimeout(() => {
      setShouldRender(false);
      setLastMessage(null);
    }, 300);
    return () => clearTimeout(hideTimer);
  }, [message]);

  useEffect(() => {
    if (message && onClose && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, onClose, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      setLastMessage(null);
      onClose?.();
    }, 300);
  };

  if (!shouldRender || !lastMessage) return null;

  return (
    <div
      className={`success-message success-message-${variant || 'default'} ${isVisible ? "visible" : "hidden"}`}
      role="alert"
    >
      <CheckCircle className="success-icon" />
      <span className="success-text">{lastMessage}</span>
      {onClose && (
        <button
          onClick={handleClose}
          className="success-close-button"
        >
          <X className="close-icon" />
          <span className="sr-only">Close</span>
        </button>
      )}
    </div>
  );
};

export default SuccessMessage;
